import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Select,
  Input,
  Button,
  useToast,
  Spinner,
  Flex,
  FormHelperText,
  Badge,
  HStack,
  Divider,
  Switch
} from '@chakra-ui/react';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';
import { useFeatureFlags } from '../FeatureFlags';

const IonmBaselineConfig = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Current saved state
  const [savedConfig, setSavedConfig] = useState(null);

  // Form state
  const [strategy, setStrategy] = useState('none');
  const [unitStrategy, setUnitStrategy] = useState('match_units');
  const [cptCodes, setCptCodes] = useState('95870, 95869, 95867, 95865, 95868, 95861, 51785');
  const [suppressOnPainCases, setSuppressOnPainCases] = useState(false);
  const [painKeywords, setPainKeywords] = useState('ablation, intracept, spinal cord stimulator, scs, minuteman');
  const [strictPainKeywords, setStrictPainKeywords] = useState('basivertebral nerve ablation, intracept procedure, spinal cord stimulator implant');
  const [painExclusionKeywords, setPainExclusionKeywords] = useState('laminectomy, fusion, discectomy, arthrodesis, corpectomy, foraminotomy');
  const [painDurationThreshold, setPainDurationThreshold] = useState(20);

  const { accessToken } = useContext(AppContext);
  const { featureFlags } = useFeatureFlags();
  const toast = useToast();

  const fetchConfig = async () => {
    if (!accessToken) return;
    
    setIsLoading(true);
    try {
      const response = await callApi(`/api/admin/config/org/ionm-baseline`, accessToken);
      if (response.ok) {
        const data = await response.json();
        const config = data.ionm_baseline || {};
        
        // Save the exact state from the DB for the "Current Configuration" display
        setSavedConfig(config);
        
        // Populate the form
        setStrategy(config.strategy || 'none');
        setUnitStrategy(config.unit_strategy || 'match_units');
        setSuppressOnPainCases(config.suppress_on_pain_cases || false);
        
        if (config.cpt_codes && Array.isArray(config.cpt_codes)) {
          setCptCodes(config.cpt_codes.join(', '));
        } else {
          setCptCodes('95870, 95869, 95867, 95865, 95868, 95861, 51785');
        }

        if (config.pain_keywords && Array.isArray(config.pain_keywords)) {
          setPainKeywords(config.pain_keywords.join(', '));
        } else {
          setPainKeywords('ablation, intracept, spinal cord stimulator, scs, minuteman');
        }

        if (config.strict_pain_keywords && Array.isArray(config.strict_pain_keywords)) {
          setStrictPainKeywords(config.strict_pain_keywords.join(', '));
        } else {
          setStrictPainKeywords('basivertebral nerve ablation, intracept procedure, spinal cord stimulator implant');
        }

        if (config.pain_exclusion_keywords && Array.isArray(config.pain_exclusion_keywords)) {
          setPainExclusionKeywords(config.pain_exclusion_keywords.join(', '));
        } else {
          setPainExclusionKeywords('laminectomy, fusion, discectomy, arthrodesis, corpectomy, foraminotomy');
        }

        setPainDurationThreshold(config.pain_duration_threshold !== undefined ? config.pain_duration_threshold : 20);
      }
    } catch (error) {
        toast({
          title: 'Error fetching configuration',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

  useEffect(() => {
    fetchConfig();
  }, [accessToken, toast]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Parse CPT codes string into array
      const codesArray = cptCodes
        .split(',')
        .map(code => code.trim())
        .filter(code => code.length > 0);

      const painKeywordsArray = painKeywords
        .split(',')
        .map(kw => kw.trim())
        .filter(kw => kw.length > 0);

      const strictPainKeywordsArray = strictPainKeywords
        .split(',')
        .map(kw => kw.trim())
        .filter(kw => kw.length > 0);

      const painExclusionKeywordsArray = painExclusionKeywords
        .split(',')
        .map(kw => kw.trim())
        .filter(kw => kw.length > 0);

      const parsedThreshold = parseInt(painDurationThreshold, 10);
      
      const payload = {
        ionm_baseline: {
          strategy,
          unit_strategy: unitStrategy,
          cpt_codes: codesArray,
          suppress_on_pain_cases: suppressOnPainCases,
          pain_keywords: painKeywordsArray,
          strict_pain_keywords: strictPainKeywordsArray,
          pain_exclusion_keywords: painExclusionKeywordsArray,
          pain_duration_threshold: isNaN(parsedThreshold) ? 20 : parsedThreshold
        }
      };

      const response = await callApi(`/api/admin/config/org/ionm-baseline`, accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save configuration');
      }

      toast({
        title: 'Configuration saved',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh the current configuration display
      await fetchConfig();
      
    } catch (error) {
      toast({
        title: 'Error saving configuration',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" p={10}>
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  return (
    <Box bg="white" borderRadius="md" boxShadow="sm" borderWidth="1px" p={6}>
      <VStack spacing={8} align="stretch" maxW="800px">
        
        {/* Current Configuration Display */}
        <Box bg="gray.50" p={4} borderRadius="md" borderWidth="1px">
          <Heading size="sm" mb={4} color="gray.700">Current Active Configuration</Heading>
          {isLoading ? (
            <Spinner size="sm" />
          ) : !savedConfig || Object.keys(savedConfig).length === 0 ? (
            <Text fontSize="sm" color="gray.500">No configuration found. Using system defaults.</Text>
          ) : (
            <VStack align="stretch" spacing={3}>
              <HStack>
                <Text fontSize="sm" fontWeight="bold" w="150px">Strategy:</Text>
                <Badge colorScheme={savedConfig.strategy === 'none' ? 'red' : 'green'}>
                  {savedConfig.strategy || 'none'}
                </Badge>
              </HStack>
              <HStack>
                <Text fontSize="sm" fontWeight="bold" w="150px">Unit Strategy:</Text>
                <Text fontSize="sm">{savedConfig.unit_strategy || 'match_units'}</Text>
              </HStack>
              <HStack align="flex-start">
                <Text fontSize="sm" fontWeight="bold" w="150px">Target Codes:</Text>
                <Flex flexWrap="wrap" gap={2}>
                  {savedConfig.cpt_codes && savedConfig.cpt_codes.length > 0 ? (
                    savedConfig.cpt_codes.map(code => (
                      <Badge key={code} colorScheme="blue" variant="outline">{code}</Badge>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.500">None</Text>
                  )}
                </Flex>
              </HStack>
              <HStack>
                <Text fontSize="sm" fontWeight="bold" w="150px">Suppress on Pain Cases:</Text>
                <Badge colorScheme={savedConfig.suppress_on_pain_cases ? 'green' : 'gray'}>
                  {savedConfig.suppress_on_pain_cases ? 'Enabled' : 'Disabled'}
                </Badge>
              </HStack>
              {savedConfig.suppress_on_pain_cases && (
                <>
                  <HStack align="flex-start">
                    <Text fontSize="sm" fontWeight="bold" w="150px">Pain Keywords:</Text>
                    <Flex flexWrap="wrap" gap={2}>
                      {savedConfig.pain_keywords && savedConfig.pain_keywords.length > 0 ? (
                        savedConfig.pain_keywords.map(kw => (
                          <Badge key={kw} colorScheme="purple" variant="subtle">{kw}</Badge>
                        ))
                      ) : (
                        <Text fontSize="sm" color="gray.500">None</Text>
                      )}
                    </Flex>
                  </HStack>
                  <HStack align="flex-start">
                    <Text fontSize="sm" fontWeight="bold" w="150px">Strict Keywords:</Text>
                    <Flex flexWrap="wrap" gap={2}>
                      {savedConfig.strict_pain_keywords && savedConfig.strict_pain_keywords.length > 0 ? (
                        savedConfig.strict_pain_keywords.map(kw => (
                          <Badge key={kw} colorScheme="purple" variant="outline">{kw}</Badge>
                        ))
                      ) : (
                        <Text fontSize="sm" color="gray.500">None</Text>
                      )}
                    </Flex>
                  </HStack>
                  <HStack align="flex-start">
                    <Text fontSize="sm" fontWeight="bold" w="150px">Exclusion Keywords:</Text>
                    <Flex flexWrap="wrap" gap={2}>
                      {savedConfig.pain_exclusion_keywords && savedConfig.pain_exclusion_keywords.length > 0 ? (
                        savedConfig.pain_exclusion_keywords.map(kw => (
                          <Badge key={kw} colorScheme="red" variant="subtle">{kw}</Badge>
                        ))
                      ) : (
                        <Text fontSize="sm" color="gray.500">None</Text>
                      )}
                    </Flex>
                  </HStack>
                  <HStack>
                    <Text fontSize="sm" fontWeight="bold" w="150px">Duration Threshold:</Text>
                    <Text fontSize="sm">{savedConfig.pain_duration_threshold || 20} minutes</Text>
                  </HStack>
                </>
              )}
            </VStack>
          )}
        </Box>

        <Divider />

        {/* Edit Configuration Form */}
        <Box>
          <Heading size="md" mb={2} color="gray.700">Edit Configuration</Heading>
          <Text fontSize="sm" color="gray.500" mb={6}>
            Configure how baseline CPT codes are generated for IONM cases.
          </Text>

          {isLoading ? (
            <Flex justify="center" p={10}>
              <Spinner color="blue.500" />
            </Flex>
          ) : (
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel>Baseline Strategy</FormLabel>
                <Select value={strategy} onChange={(e) => setStrategy(e.target.value)}>
                  <option value="none">None (Do not generate baselines)</option>
                  <option value="priority_first">Priority First (Baseline only the first matched code)</option>
                  <option value="all_coded">All Coded (Baseline every matched code)</option>
                </Select>
                <FormHelperText>
                  Determines whether to generate a baseline for only the highest priority code found, for all codes in the target list, or not at all.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Unit Strategy</FormLabel>
                <Select value={unitStrategy} onChange={(e) => setUnitStrategy(e.target.value)}>
                  <option value="match_units">Match Units (Copy units from original code)</option>
                  <option value="single_unit">Single Unit (Always bill 1 unit for baseline)</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Target CPT Codes</FormLabel>
                <Input 
                  value={cptCodes} 
                  onChange={(e) => setCptCodes(e.target.value)} 
                  placeholder="e.g. 95870, 95869, 51785"
                />
                <FormHelperText>
                  Comma-separated list of CPT codes eligible for baselining. Order matters if using "Priority First" strategy.
                </FormHelperText>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="suppress-pain-cases" mb="0">
                  Suppress on Pain Cases
                </FormLabel>
                <Switch 
                  id="suppress-pain-cases" 
                  isChecked={suppressOnPainCases}
                  onChange={(e) => setSuppressOnPainCases(e.target.checked)}
                />
              </FormControl>
              <Text fontSize="sm" color="gray.500" mt={-4}>
                If enabled, baseline duplication will be skipped for cases identified as pain procedures.
              </Text>

              {suppressOnPainCases && (
                <Box pl={4} borderLeftWidth="2px" borderColor="blue.200">
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel>Pain Keywords (General)</FormLabel>
                      <Input 
                        value={painKeywords} 
                        onChange={(e) => setPainKeywords(e.target.value)} 
                        placeholder="e.g. ablation, intracept, scs"
                      />
                      <FormHelperText>
                        Comma-separated list of keywords to search for in the case description.
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Strict Pain Keywords (Fallback)</FormLabel>
                      <Input 
                        value={strictPainKeywords} 
                        onChange={(e) => setStrictPainKeywords(e.target.value)} 
                        placeholder="e.g. basivertebral nerve ablation"
                      />
                      <FormHelperText>
                        Comma-separated list of stricter keywords to search for in the full case text if general keywords are not found.
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Exclusion Keywords</FormLabel>
                      <Input 
                        value={painExclusionKeywords} 
                        onChange={(e) => setPainExclusionKeywords(e.target.value)} 
                        placeholder="e.g. laminectomy, fusion, discectomy"
                      />
                      <FormHelperText>
                        Comma-separated list of keywords that will ALWAYS prevent a case from being flagged as a pain case, regardless of duration.
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Duration Threshold (Minutes)</FormLabel>
                      <Input 
                        type="number"
                        value={painDurationThreshold} 
                        onChange={(e) => setPainDurationThreshold(e.target.value)} 
                      />
                      <FormHelperText>
                        The case must be shorter than this duration to be flagged as a pain case.
                      </FormHelperText>
                    </FormControl>
                  </VStack>
                </Box>
              )}

              <Button 
                colorScheme="blue" 
                onClick={handleSave} 
                isLoading={isSaving}
                alignSelf="flex-start"
                mt={4}
              >
                Save Configuration
              </Button>
            </VStack>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

export default IonmBaselineConfig;